import { Button, Icon, IconName } from '@unmind/design-system';
import React, { RefObject } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import RoutePath from 'App/RoutePath';
import { useUserEntitlements } from 'hooks/useUserEntitlements';
import Modal from 'Shared/Modal';
import { getChannel } from 'utils';

import { ModalContent } from './styles';

export type ModalProps = {
  isOpen: boolean;
  onClose(): void;
  parentRef: RefObject<HTMLElement>;
  testId?: string;
};

const TalkModalContent = () => {
  const { t } = useTranslation('book', {
    keyPrefix: 'book.banner.talk.modal',
  });
  const theme = useTheme();
  const { entitlements } = useUserEntitlements();
  const { supportUrl } = getChannel();

  const keys = ['serviceFor', 'serviceNotFor'] as const;

  return (
    <ModalContent>
      <h2>{t('title')}</h2>
      {keys.map(key => (
        <>
          <p className="title">
            <Icon
              iconName={
                key === 'serviceFor'
                  ? IconName.CheckCircle
                  : IconName.AlertTriangle
              }
              colors={{
                primary: theme.colors.text.default,
                secondary: theme.colors.modal.background,
              }}
            />
            {t(`${key}.title`)}
          </p>
          <ul>
            <Trans
              t={t}
              i18nKey={`${key}.text`}
              components={{
                li: <li />,
              }}
            />
          </ul>
        </>
      ))}
      <p>
        {/* eslint-disable jsx-a11y/anchor-has-content */}
        <Trans
          t={t}
          i18nKey="text"
          components={[
            <a key="link" href={supportUrl} rel="noopener noreferrer" />,
          ]}
        />
        {/* eslint-enable jsx-a11y/anchor-has-content */}
      </p>
      <aside>
        <p>
          <Trans
            t={t}
            i18nKey="immediateHelp.text"
            components={{
              strong: <strong />,
            }}
          />
        </p>
        <Button
          variant="primary"
          size={'small'}
          label={
            <>
              <Icon
                iconName={IconName.Phone}
                colors={{ primary: theme.button.primary.default.textColor }}
              />
              {t('immediateHelp.cta')}
            </>
          }
          to={`${RoutePath.Help}${
            entitlements.unmindHelp ? '#immediate-support' : ''
          }`}
        />
      </aside>
    </ModalContent>
  );
};

const TalkModal = ({
  isOpen,
  onClose,
  parentRef,
  testId = 'talk-modal',
}: ModalProps) => (
  <Modal
    testId={testId}
    open={isOpen}
    onCloseCallback={onClose}
    closeHeader
    modalContent={() => <TalkModalContent />}
    size="medium"
    returnFocusTo={parentRef}
  />
);

export default TalkModal;
